import {
  getFullUrl,
  getSessionData,
  isValidURL,
  unixEpoch,
} from "@repo/shared-utils/common-utils";
import {
  SSORedirectArgs,
  SSORedirectArgsBase,
  SSORedirectArgsWithPerpetualSessionData,
  SSORedirectArgsWithSessionData,
  SSOURLConfigs,
} from "../types/SSO.types";
import { setIsCommonSsoModalVisible, store } from "@repo/web-store";
import { LoginConstants } from "@repo/shared-utils/sso.constants";
import { callHandler } from "./handler-utils";

/**
 * getAndDeleteParam
 * @param url
 * @param paramName
 * @returns
 */
function getAndDeleteParam(url: URL, paramName: string): string | null {
  const param = url.searchParams.get(paramName);
  if (param) {
    url.searchParams.delete(paramName);
  }
  return param;
}

/**
 * getSSOConfigsFromUrl
 * @param href
 * @returns
 */
export function getSSOConfigsFromUrl(href: string): SSOURLConfigs {
  const fullUrl = isValidURL(href) ? href : getFullUrl(href);
  const url = new URL(fullUrl);
  const newUrlPathname = getAndDeleteParam(url, "v1");
  const ssoModule = getAndDeleteParam(url, "sso-module");
  const ssoProduct = getAndDeleteParam(url, "sso-product")?.split(",") ?? null;
  const ssoResume = getAndDeleteParam(url, "sso-resume");
  const ssoFormPostUrl = getAndDeleteParam(url, "sso-form-post");
  const oldUrl = url;
  const newUrl = new URL(url);
  newUrl.pathname = newUrlPathname ?? oldUrl.pathname;
  return { fullUrl, newUrl, ssoProduct, ssoModule, ssoResume, ssoFormPostUrl };
}

/**
 * handleLoginSso
 * @param mapping
 * @param data
 */
const handleLoginSso = async (
  mapping: SSOURLConfigs,
  data: SSORedirectArgs,
  loginStatus: string,
) => {
  const { mobileNo } = await getSessionData();
  const ssoModuleArray = [
    "myaccount",
    "bajajmall",
    "ondc",
    "offerworld",
    "commonsso",
    "bfhl",
    "bajajsecurities",
  ];
  if (
    loginStatus === "success" ||
    (mapping.ssoModule && ssoModuleArray.includes(mapping.ssoModule))
  ) {
    callHandler(mapping, {
      ...data,
      data: { ...data.data, loginTimestamp: unixEpoch() },
    });
  } else {
    try {
      store.dispatch(setIsCommonSsoModalVisible(true));
      const { loginUser } = await import("./user.ts");
      await loginUser(mobileNo.length ? mobileNo : undefined);
      store.dispatch(setIsCommonSsoModalVisible(false));
      callHandler(mapping, {
        ...data,
        data: {
          ...data.data,
          loginTimestamp: unixEpoch(),
          awaitJourney: true,
        },
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
};

/**
 * handlePerpetualSso
 * @param mapping
 * @param data
 */
const handlePerpetualSso = async (
  mapping: SSOURLConfigs,
  data: SSORedirectArgs,
) => {
  try {
    const { mobileNo } = await getSessionData();
    const ssoModuleArray = ["myaccount", "bajajmall", "offerworld"];
    if (mapping.ssoModule && ssoModuleArray.includes(mapping.ssoModule)) {
      callHandler(mapping, data);
    } else {
      store.dispatch(setIsCommonSsoModalVisible(true));
      const { loginUser } = await import("./user.ts");
      await loginUser(mobileNo);
      store.dispatch(setIsCommonSsoModalVisible(false));
      callHandler(mapping, {
        ...data,
        data: {
          ...data.data,
          loginTimestamp: unixEpoch(),
          awaitJourney: true,
        },
      });
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * This function handles Single Sign-On (SSO) redirection based on the provided data and module mapping.
 * There are three different cases:
 *	1. Logged in user: Checks cart based on `checkCart` data property, if an item is there shows resume journey modal.
 *	2. Perpetual user: For myaccount and bajajmall, user is redirected directly, for all other modules, user is logged
 *	    in first and then it is similar to logged in case.
 *	3. Logged out user: User is asked to login first, then it is same as logged in case.
 * @param {SSORedirectArgsBase} data - The base arguments for the SSO redirection. This includes the href of the redirection URL.
 * @param {V1ModuleMapping} [mappingArg] - The module mapping for the redirection. If not provided, the function will retrieve the module mapping from the href in the data.
 *
 * @returns A Promise that resolves when the SSO redirection is complete.
 *
 * @throws If any error occurs during the process.
 */
export const ssoRedirect = async (
  data: SSORedirectArgsBase,
  loginStatus: string,
  mappingArg?: SSOURLConfigs,
) => {
  const mapping = mappingArg ?? getSSOConfigsFromUrl(data.href);
  try {
    if (loginStatus === LoginConstants.PERPETUAL) {
      const perpetualSessionData = await getSessionData();
      handlePerpetualSso(mapping, {
        ...data,
        isPerpetual: true,
        perpetualSessionData,
        sessionData: undefined,
        mapping,
      } as SSORedirectArgsWithPerpetualSessionData);
    } else if (mapping.ssoModule) {
      const sessionData = await getSessionData();
      await handleLoginSso(
        mapping,
        {
          ...data,
          isPerpetual: false,
          perpetualSessionData: undefined,
          sessionData,
          mapping,
        } as SSORedirectArgsWithSessionData,
        loginStatus,
      );
    } else {
      // If no sso module mapping is found, open the URL as per the target.
      window.open(data.href, data.target);
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};
